import { useState, useCallback, useRef, useEffect } from 'react';
import { useObserver } from 'mobx-react-lite';
import { useApolloClient, useQuery } from '@apollo/client';

import useStores from 'shared/hooks/use-stores';
import { usePublicContextSessionPusher } from 'src/hooks/use-public-context-session-pusher';
import { GetPublicContextSessionDocument, MeConsumerDocument, PublicContextSessionStatus } from 'src/gql/graphql';
import { parseSessionStatus } from 'src/components/kiosk-pro/kiosk-pro-shared/session-status';
import {
  useGetSessionSecret,
  removePublicSessionSecret,
} from 'src/components/kiosk-pro/kiosk-pro-shared/use-session-secret';

type UsePublicSessionLoginHandlerReturnType = {
  sessionState: {
    status: PublicContextSessionStatus;
    hasAttemptedLogin: boolean;
  };
  isUserLoggedInSuccessfully: boolean;
  userFirstName: string;
};

export function usePublicSessionLoginHandler(sessionId: string): UsePublicSessionLoginHandlerReturnType {
  const { User } = useStores();
  const apolloClient = useApolloClient();
  const [sessionState, setSessionState] = useState({
    status: PublicContextSessionStatus.unclaimed,
    hasAttemptedLogin: false,
  });
  const isLoggingInRef = useRef(false);
  const hasAttemptedLoginRef = useRef(false);

  const isUserLoggedIn = useObserver(() => User?.isLoggedIn ?? false);
  const userFirstName = useObserver(() => User?.firstName ?? '');
  const birthday = useObserver(() => User.birthday);
  const phone = useObserver(() => User.phone);

  const hasUserLoggedInAndClaimedStatus = sessionState.status === PublicContextSessionStatus.claimed && isUserLoggedIn;

  // ECOMM-10367: present the creator secret so Armageddon returns the JWT to this
  // display (the session's creator). Without it, accessToken comes back null.
  const secret = useGetSessionSecret(sessionId);

  const { data, refetch } = useQuery(GetPublicContextSessionDocument, {
    variables: {
      id: sessionId,
      secret,
    },
    pollInterval: 15000,
  });

  usePublicContextSessionPusher({
    sessionId,
    enabled: !hasUserLoggedInAndClaimedStatus,
    onSessionUpdated: (sessionData) => {
      setSessionState((prev) => ({
        ...prev,
        status: parseSessionStatus(sessionData.status),
      }));
      void refetch();
    },
    onSessionClaimed: (claimData) => {
      setSessionState((prev) => ({
        ...prev,
        status: parseSessionStatus(claimData.status),
      }));
    },
  });

  const isUserLoggedInSuccessfully = hasUserLoggedInAndClaimedStatus && birthday && phone;

  useEffect(() => {
    if (hasUserLoggedInAndClaimedStatus) {
      void User.refetch();
    }
  }, [hasUserLoggedInAndClaimedStatus, User]);

  // Continue polling User data until birthday and phone are available. They will be updated from the public session on consumers phone.
  useEffect(() => {
    if (hasUserLoggedInAndClaimedStatus && (!birthday || !phone)) {
      const interval = setInterval(() => {
        void User.refetch();
      }, 2000);

      return () => clearInterval(interval);
    }
    return undefined;
  }, [hasUserLoggedInAndClaimedStatus, birthday, phone, User]);

  const handleUserLogin = useCallback(async (): Promise<void> => {
    if (!data?.getPublicContextSession?.accessToken || hasAttemptedLoginRef.current || isLoggingInRef.current) {
      return;
    }

    try {
      isLoggingInRef.current = true;

      // First get the user data using the access token
      const { data: userData } = await apolloClient.query({
        query: MeConsumerDocument,
        fetchPolicy: 'no-cache',
        context: {
          headers: {
            authorization: data.getPublicContextSession.accessToken,
          },
        },
      });

      // Then login with both the access token and user data
      await User.login({
        accessToken: data.getPublicContextSession.accessToken,
        user: userData.meConsumer,
      });

      hasAttemptedLoginRef.current = true;
      setSessionState((prev) => ({ ...prev, hasAttemptedLogin: true }));
    } catch (loginError) {
      console.error('Error logging in user:', loginError);
      hasAttemptedLoginRef.current = true;
      setSessionState((prev) => ({ ...prev, hasAttemptedLogin: true }));
    } finally {
      isLoggingInRef.current = false;
      // ECOMM-10367: once we've attempted login, the secret has served its purpose either
      // way — on success it's spent, and on failure we don't retry (hasAttemptedLoginRef
      // guards re-entry), so there's no reason to keep it. Drop it regardless of outcome.
      removePublicSessionSecret(sessionId);
    }
  }, [data?.getPublicContextSession?.accessToken, apolloClient, User, sessionId]);

  useEffect(() => {
    void handleUserLogin();

    const publicContextSession = data?.getPublicContextSession;
    if (publicContextSession?.status) {
      setSessionState((prev) => ({
        ...prev,
        status: publicContextSession.status,
      }));
    }
  }, [handleUserLogin, data]);

  return {
    sessionState,
    isUserLoggedInSuccessfully,
    userFirstName,
  };
}
